import { useTranslation } from 'react-i18next'
import { mdiTimerSand } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { useLocale } from '../../contexts/LocaleContext'
import { formatDuration } from '../../lib/duration'
import type { ImportTiming } from './importTypes'

interface ImportIngestionTimeStatProps {
    timing: ImportTiming
}

/**
 * "How long did it take?" (spec 2026-10-27-11 §5): the machine time from the
 * deposit to the structured portfolio, people's time (the wait for the go)
 * apart, the four figures asked for, and a disclosure of where the time goes.
 *
 * Rendered only when the summary carries `timing`: an import older than the
 * feature was not measured, and "0 s" would be a confident wrong answer.
 */
export function ImportIngestionTimeStat({ timing }: ImportIngestionTimeStatProps) {
    const { t } = useTranslation('imports')
    const { locale } = useLocale()
    const lang = locale
    const d = (ms: number) => formatDuration(ms, lang)
    const time = (iso: string) => new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })

    const waitingForGo = timing.waiting_for_go_since
        ? Math.max(0, Date.now() - new Date(timing.waiting_for_go_since).getTime())
        : null
    const docs = timing.documents

    return (
        <section
            data-testid="import-ingestion-time"
            className="flex flex-col gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm"
        >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <AppIcon tone="muted" path={mdiTimerSand} size="md" className="shrink-0" />
                {waitingForGo !== null ? (
                    <span className="text-muted-foreground" data-testid="import-ingestion-time-go-wait">
                        {t('ingestion_time.waiting_for_go', { read: d(timing.read_ms), wait: d(waitingForGo) })}
                    </span>
                ) : (
                    <>
                        <span className="text-muted-foreground">{t('ingestion_time.title')}</span>
                        <span className="font-semibold text-foreground" data-testid="import-ingestion-time-machine">
                            {timing.live ? t('ingestion_time.in_progress', { value: d(timing.elapsed_ms) }) : d(timing.machine_ms)}
                        </span>
                        <span className="text-muted-foreground" data-testid="import-ingestion-time-read">
                            · {t('ingestion_time.read')} {d(timing.read_ms)}
                        </span>
                        {!timing.live && (
                            <span className="text-muted-foreground" data-testid="import-ingestion-time-extraction">
                                · {t('ingestion_time.extraction')} {d(timing.extraction_ms)}
                            </span>
                        )}
                        {timing.go_wait_ms > 0 && (
                            <span className="text-muted-foreground" data-testid="import-ingestion-time-go-wait">
                                · {t('ingestion_time.go_wait')} {d(timing.go_wait_ms)}
                            </span>
                        )}
                    </>
                )}
                <span className="text-muted-foreground" data-testid="import-ingestion-time-documents">
                    · {t('ingestion_time.documents', { count: docs.count })}
                    {docs.pages > 0 && `, ${t('ingestion_time.pages', { count: docs.pages })}`}
                    {docs.settled > 0 && `, ${t('ingestion_time.median', { value: d(docs.p50_machine_ms) })}, ${t('ingestion_time.p95', { value: d(docs.p95_machine_ms) })}`}
                </span>
            </div>

            {!timing.live && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                    <span data-testid="import-ingestion-time-read-extract">
                        {t('ingestion_time.figure_read_extract')} <b className="text-foreground">{d(timing.figures.read_extract_ms)}</b>
                    </span>
                    <span data-testid="import-ingestion-time-generation">
                        {t('ingestion_time.figure_generate')} <b className="text-foreground">{d(timing.figures.generate_ms)}</b>
                    </span>
                    <span data-testid="import-ingestion-time-agents">
                        {t('ingestion_time.figure_agents')} <b className="text-foreground">{d(timing.figures.agents_ms)}</b>
                    </span>
                    <span data-testid="import-ingestion-time-total">
                        {t('ingestion_time.figure_total')} <b className="text-foreground">{d(timing.figures.total_ms)}</b>
                    </span>
                </div>
            )}

            {timing.after_extraction.documents > 0 && timing.after_extraction.last_end && (
                <div className="text-muted-foreground" data-testid="import-ingestion-time-late">
                    {t('ingestion_time.late', { count: timing.after_extraction.documents, time: time(timing.after_extraction.last_end) })}
                </div>
            )}

            {(timing.by_job_type.length > 0 || timing.tiers.length > 0) && (
                <details data-testid="import-ingestion-time-details">
                    <summary className="cursor-pointer text-muted-foreground">{t('ingestion_time.details')}</summary>
                    {timing.tiers.length > 0 && (
                        <ul className="mt-2 flex flex-col gap-1" data-testid="import-ingestion-time-tiers">
                            {timing.tiers.map((tier) => (
                                <li key={tier.tier} className="text-muted-foreground">
                                    <b className="text-foreground">{t('ingestion_time.tier', { tier: tier.tier })}</b>{' '}
                                    {t('ingestion_time.tier_line', {
                                        released: time(tier.released_at),
                                        settled: tier.settled_at ? time(tier.settled_at) : '—',
                                    })}
                                </li>
                            ))}
                        </ul>
                    )}
                    {timing.by_job_type.length > 0 && (
                        <table className="mt-2 w-full text-left" data-testid="import-ingestion-time-by-job-type">
                            <caption className="sr-only">{t('ingestion_time.by_job_type')}</caption>
                            <thead>
                                <tr className="text-muted-foreground">
                                    <th>{t('ingestion_time.col_job_type')}</th>
                                    <th>{t('ingestion_time.col_run')}</th>
                                    <th>{t('ingestion_time.col_p95')}</th>
                                    <th>{t('ingestion_time.col_queue')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {timing.by_job_type.map((row) => (
                                    <tr key={row.job_type} data-testid={`import-ingestion-time-job-${row.job_type}`}>
                                        <td>{row.job_type}</td>
                                        <td>{d(row.run_ms)}</td>
                                        <td>{d(row.p95_run_ms)}</td>
                                        <td>{d(row.queue_ms)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </details>
            )}
        </section>
    )
}
